import apiClient from '../apiClient';
import type { BaseResponse, PaginationRequest, ListResponse } from '../../types/base.dto';
import type { OrderData } from '../../types/process/order-data';
import type { InstallmentDetail } from '../../types/process/installment-detail';
import type {
  OverdueDashboardSummary,
  StoreStatistics,
  OverdueRateAnalysisData,
  OverdueLossRateAnalysisData,
  BadDebtRateAnalysisData,
  BadDebtLossRateAnalysisData,
  OverdueBillDashboardSummary
} from '../../types/entity/overdue';
import type { OrderDataAggregatePeriodFlat } from '../../types/entity/overdue';

/**
 * 逾期分析请求参数接口
 */
export interface OverdueAnalysisRequest {
  orderFilter: { [key: string]: any };
  installmentsFilter: { [key: string]: any };
}

/**
 * 逾期订单运营看板响应数据
 */
export interface OverdueOrderDashboardResponse extends ListResponse<OrderData> {
  summary: OverdueDashboardSummary;
}

/**
 * 店铺聚合统计请求参数
 */
interface StoreAggregateStatisticsRequest {
  storeIds?: number[];
  startDate?: string;
  endDate?: string;
}

/**
 * 店铺聚合统计响应数据
 */
export interface StoreAggregateStatisticsResponse {
  statistics: StoreStatistics[];
  summary: StoreStatistics;
  updateTime: string;
}

/**
 * 逾期标签操作请求参数
 */
export interface OverdueTagRequest {
  orderNo: string;
  value: 'true' | 'false' | 'remove';
  reason?: string;
}

/**
 * 逾期相关 API 服务类
 * 根据后端实际支持的接口进行精简
 */
export interface OverdueCheckDiffItem {
  orderNo: string;
  serverId: number;
  source: 'orderData' | 'overdue';
  tag?: string;
  checkTime?: string;
}

export interface AnalysisItem {
  period: number;
  overdue: OrderDataAggregatePeriodFlat;
  origin: OrderDataAggregatePeriodFlat;
}

export class OverdueAPI {
  
  // ===== 逾期分析相关接口 =====
  
  /**
   * 获取逾期率分析数据
   * 对应后端路由: POST /overdue/analysis-rate
   */
  async getOverdueRateAnalysis(params: OverdueAnalysisRequest): Promise<BaseResponse<OverdueRateAnalysisData[]>> {
    const response = await apiClient.post('/overdue/analysis-rate', params, { timeout: 100000 });
    return response.data;
  }

  /**
   * 获取逾期资损率分析数据
   * 对应后端路由: POST /overdue/analysis-loss-rate
   */
  async getOverdueLossRateAnalysis(params: OverdueAnalysisRequest): Promise<BaseResponse<OverdueLossRateAnalysisData[]>> {
    const response = await apiClient.post('/overdue/analysis-loss-rate', params, { timeout: 100000 });
    return response.data;
  }

  /**
   * 获取坏账率分析数据
   * 对应后端路由: POST /overdue/analysis-bad-debt-rate
   */
  async getBadDebtRateAnalysis(params: OverdueAnalysisRequest): Promise<BaseResponse<BadDebtRateAnalysisData[]>> {
    const response = await apiClient.post('/overdue/analysis-bad-debt-rate', params, { timeout: 100000 });
    return response.data;
  }

  /**
   * 获取坏账资损率分析数据
   * 对应后端路由: POST /overdue/analysis-bad-debt-loss-rate
   */
  async getBadDebtLossRateAnalysis(params: OverdueAnalysisRequest): Promise<BaseResponse<BadDebtLossRateAnalysisData[]>> {
    const response = await apiClient.post('/overdue/analysis-bad-debt-loss-rate', params, { timeout: 100000 });
    return response.data;
  }

  /**
   * 订单维度聚合分析（按当前期分组，字段摊平）
   * 对应后端路由: POST /overdue/analysis-order-aggregate
   */
  async getOrderAggregateAnalysis(params: { filter: Record<string, any>; metric: 'count' | 'amount' | 'loss' | 'bad_debt_count' | 'bad_debt_amount'; type?: 'overdue' | 'bad_debt' }): Promise<BaseResponse<AnalysisItem[]>> {
    const response = await apiClient.post('/overdue/analysis-order-aggregate', params, { timeout: 100000 });
    return response.data;
  }

  // ===== 逾期订单运营看板相关接口 =====
  
  /**
   * 获取逾期订单运营看板数据
   * 对应后端路由: POST /overdue/dashboard-orders
   */
  async getOverdueOrdersDashboard(params: PaginationRequest): Promise<OverdueOrderDashboardResponse> {
    const response = await apiClient.post('/overdue/dashboard-orders', params, { timeout: 100000 });
    return response.data;
  }

  /**
   * 获取逾期订单运营看板汇总（仅 summary）
   * 对应后端路由: POST /overdue/dashboard-orders/summary
   */
  async getOverdueSummary(params: PaginationRequest): Promise<OverdueDashboardSummary> {
    const response = await apiClient.post('/overdue/summary', params, { timeout: 100000 });
    return response.data.data;
  }

  /**
   * 获取逾期账单看板汇总（Bills）
   * 对应后端路由: POST /overdue/bills-summary
   */
  async getOverdueBillsSummary(params: InstallmentOrderJoinedListRequest): Promise<OverdueBillDashboardSummary> {
    const response = await apiClient.post('/overdue/bills-summary', params, { timeout: 100000 });
    return response.data.data;
  }

  // ===== 店铺聚合统计相关接口 =====
  
  /**
   * 获取店铺聚合统计数据
   * 对应后端路由: GET /overdue/statistics/stores/aggregate
   */
  async getStoreAggregateStatistics(params?: StoreAggregateStatisticsRequest): Promise<StoreAggregateStatisticsResponse> {
    const response = await apiClient.post('/overdue/statistics', params);
    return response.data.data;
  }

  // ===== 逾期标签管理相关接口 =====
  
  /**
   * 更新订单逾期标签
   * 对应后端路由: POST /overdue/tag
   */
  async upsertOverdueTag(params: OverdueTagRequest): Promise<BaseResponse<null>> {
    const response = await apiClient.post('/overdue/tag', params, { timeout: 100000 });
    return response.data;
  }

  /**
   * 获取订单逾期校验差异列表
   * 对应后端路由: GET /overdue/checks/order-overdue/diff-list
   * 可选参数: serverId
   */
  async getOrderOverdueCheckDiffList(params?: { serverId?: number }): Promise<BaseResponse<OverdueCheckDiffItem[]>> {
    const response = await apiClient.get('/overdue/checks/order-overdue/diff-list', {
      params: params || {},
      timeout: 100000,
    });
    return response.data;
  }

  // ===== 分期联表查询（installments + order） =====

  /**
   * 联表列表请求（以 installments 为主）
   * 对应后端路由: POST /overdue/installments-joined-list
   */
  async getInstallmentsJoinedList(params: InstallmentOrderJoinedListRequest): Promise<BaseResponse<ListResponse<RepaymentLedgerJoined>>> {
    const response = await apiClient.post('/overdue/installments', params, { timeout: 100000 });
    return response.data;
  }

  /**
   * 提醒交租订单列表
   * 对应后端路由: POST /overdue/bill-remind-list
   */
  async getBillRemindList(params: InstallmentOrderJoinedListRequest): Promise<BaseResponse<ListResponse<RepaymentLedgerJoined>>> {
    const response = await apiClient.post('/overdue/bill-remind-list', params);
    return response.data;
  }

  /**
   * 导出提醒交租订单列表
   * 对应后端路由: POST /overdue/bill-remind-list/export
   */
  async exportBillRemindList(params: InstallmentOrderJoinedListRequest): Promise<BaseResponse<any>> {
    const response = await apiClient.post('/overdue/bill-remind-list/export', params);
    return response.data;
  }
}

// 创建实例
export const overdueApi = new OverdueAPI();

// 为了保持向后兼容性，也导出为 overdueAnalysisApi
export const overdueAnalysisApi = overdueApi;

export default overdueApi;

// ====== 新增类型：分期联表查询 ======
export interface RepaymentLedgerJoined {
  installmentDetail: InstallmentDetail;
  orderData?: OrderData;
  order?: {
    orderStatus?: number;
    [key: string]: any;
  };
}

export interface InstallmentOrderJoinedListRequest extends PaginationRequest<{
  order?: Record<string, any>;
  installments?: Record<string, any>;
}> {}
